import { useState, useEffect } from "react";
import { formatPairLabel } from "../data/pairs";

/* ---------------------------------------------------------------
   SIGNALS BOT — POST /bot/signal/ { asset, deal_type } -> a Forex
  signal { asset, direction, entry_price, expiration, time_seconds,
  entry_section, comment }.
   The signal is shown full-screen (ActiveSignalOverlay) and blocks
  navigation until its countdown (derived from `time_seconds` or `expiration`, see
   utils/time.js#timeframeToSeconds) elapses; the user then must
   record the outcome via POST /bot/stats/, which is what actually
   closes the overlay.

   Persistence: the active signal (server payload + the pair it was
   requested for + the wall-clock timestamp it started at) is mirrored
   to localStorage as soon as it's received, and cleared only once a
   result is submitted. If the Telegram WebView gets suspended/reloaded
   while backgrounded, or the layout crosses the wide/narrow breakpoint
   and remounts screens, the countdown is recomputed from that stored
   start time instead of disappearing — this hook (used once, from
   App()) plus screens/signals/ActiveSignalOverlay.jsx are what make
   that work.
------------------------------------------------------------------ */

const ACTIVE_SIGNAL_STORAGE_KEY = "pj_active_signal";

function loadStoredSignal() {
  try {
    const raw = localStorage.getItem(ACTIVE_SIGNAL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.signal || !parsed?.startedAt || !parsed?.requestedPair) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

function storeSignal(entry) {
  try {
    localStorage.setItem(ACTIVE_SIGNAL_STORAGE_KEY, JSON.stringify(entry));
  } catch (e) {}
}

function clearStoredSignal() {
  try {
    localStorage.removeItem(ACTIVE_SIGNAL_STORAGE_KEY);
  } catch (e) {}
}

/* Countdown is driven off an absolute deadline (wall-clock ms) rather
   than a duration measured from mount time. That makes it self-correct
   after the tab/WebView gets throttled or fully suspended in the
   background — on the next tick (or the very next mount, e.g. after a
   Telegram reload) it just recomputes from Date.now(), so elapsed time
   during backgrounding is never lost and never "un-counts". Safe against
   missing or NaN deadlines. */
export function useCountdown(deadlineMs) {
  const [remaining, setRemaining] = useState(() => {
    if (!deadlineMs || isNaN(deadlineMs)) return 0;
    return Math.max(0, Math.round((deadlineMs - Date.now()) / 1000));
  });
  useEffect(() => {
    if (!deadlineMs || isNaN(deadlineMs)) {
      setRemaining(0);
      return;
    }
    const tick = () => {
      setRemaining(Math.max(0, Math.round((deadlineMs - Date.now()) / 1000)));
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [deadlineMs]);
  return remaining;
}

export function formatCountdown(sec) {
  if (!sec || isNaN(sec) || sec <= 0) return "00:00";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/* Owns the active-signal lifecycle for the whole app (used once from
   App(), not inside SignalsScreen) so it survives both a wide/narrow
   layout remount and a backgrounded-then-reloaded WebView: every
   mutation is mirrored to localStorage immediately, and the initial
   state is restored from there. */
export function useActiveSignal(authFetch) {
  const [entry, setEntry] = useState(() => loadStoredSignal());
  const [overlayOpen, setOverlayOpen] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [resultSubmitting, setResultSubmitting] = useState(false);
  const [resultError, setResultError] = useState(null);

  const requestSignal = async (pair, type) => {
    if (submitting || entry) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await authFetch("bot/signal/", {
        method: "POST",
        body: JSON.stringify({ asset: pair, timeframe: type, deal_type: type }),
      });
      if (!res.ok) {
        // Trial accounts are metered per (section, deal_type) bucket —
        // the backend flags that distinctly (trial_limit_reached: true,
        // 403) instead of just failing generically.
        let body = null;
        try {
          body = await res.json();
        } catch (e) {}
        if (res.status === 403 && body?.trial_limit_reached === true) {
          throw new Error("trial_limit");
        }
        throw new Error(`signal API ${res.status}`);
      }
      const data = await res.json();
      const newEntry = { signal: data, requestedPair: pair, startedAt: Date.now() };
      setEntry(newEntry);
      setOverlayOpen(true);
      storeSignal(newEntry);
    } catch (e) {
      setError(e.message || "error");
    } finally {
      setSubmitting(false);
    }
  };

  const cancelSignal = () => {
    setEntry(null);
    setError(null);
    setResultError(null);
    clearStoredSignal();
  };

  const closeOverlay = () => setOverlayOpen(false);
  const openOverlay = () => setOverlayOpen(true);

  const submitResult = async (profit) => {
    if (resultSubmitting || !entry) return;
    setResultSubmitting(true);
    setResultError(null);
    try {
      const assetLabel = formatPairLabel(entry.signal?.asset || entry.requestedPair);
      const res = await authFetch("bot/stats/", {
        method: "POST",
        body: JSON.stringify({
          asset: assetLabel,
          source: "SIGNALS",
          profit,
        }),
      });
      if (!res.ok) throw new Error(`stats API ${res.status}`);
      setEntry(null);
      clearStoredSignal();
    } catch (e) {
      setResultError(e.message || "error");
    } finally {
      setResultSubmitting(false);
    }
  };

  return {
    entry,
    overlayOpen,
    openOverlay,
    closeOverlay,
    submitting,
    error,
    requestSignal,
    resultSubmitting,
    resultError,
    submitResult,
    cancelSignal,
  };
}